import { format, formatDistanceToNow } from "date-fns";
import React, { useState, useCallback, useMemo } from "react";
import { AiOutlineFileSearch } from "react-icons/ai";
import { HiOutlineRefresh } from "react-icons/hi";
import Head from "next/head";
import Button from "../components/Button";
import FactView from "../components/FactView";
import InlineCode from "../components/InlineCode";
import LoadingPage from "../components/LoadingPage";
import { WithSidebar } from "../components/Sidebar";
import {
  EnvData,
  Fact,
  Variable,
  VariableFact,
  Query,
  Log,
} from "../src/types";
import useData from "../src/useData";
import useEnv from "../src/useEnv";
import useTenant from "../src/useTenant";
import Schedule1x1Cta from "../components/Schedule1x1Cta";
import { useRouter } from "next/router";
import Spinner from "../components/Spinner";
import { NextPage } from "next";
import { useSegmentCallback } from "../src/segment";
import GetStartedCta from "../components/GetStartedCta";
import Modal from "@/components/Modal";
import SyntaxHighlighter from "react-syntax-highlighter/dist/cjs/prism-light";
import { BACKGROUND_COLOR, THEME } from "@/components/Policy";
import MainContent from "@/components/MainContent";
import {
  MantineProvider,
  Select,
  CloseButton,
  MultiSelect,
} from "@mantine/core";
import { DateRangePicker } from "../components/DateRangePicker";
import { sharedSelectStyles } from "styles/styles";

const variableToTypedId = (variable: Variable) => {
  switch (variable.kind) {
    case "TypedId":
      return variable.value;
    case "TypedVariable":
      return { type: variable.value.type, id: "_" };
    case "FreeVariable":
      return { type: "_", id: "_" };
  }
};

function toFact(variableFact: VariableFact): Fact {
  return {
    predicate: variableFact.predicate,
    args: variableFact.args.map(variableToTypedId),
  };
}

function isNonLocalAuthorize({
  fields: {
    query: {
      fact: { predicate, args },
    },
    sql_query,
  },
}: Log): boolean {
  return (
    predicate === "allow" &&
    // WOWHACK(gj): The third arg in Authorize Resources queries with multiple
    // resources is logged as `$tag:<$count values>` if all the resources are
    // the same type and `String:[$tag0:<$count values>, $tag1:<$count values>,
    // ...]` if there are multiple types.
    args.every(
      (arg) => arg.kind === "TypedId" && !/<\d+ values>/.test(arg.value.id)
    ) &&
    !sql_query
  );
}

// TODO(killpack) DRY up / extract URL construction logic to explain page
function explainLink(query: Query, selectedEnv: EnvData | null) {
  if (selectedEnv !== null) {
    const args = query.fact.args.map((arg) =>
      arg.kind === "TypedId" ? `${arg.value.type}:${arg.value.id}` : ""
    );
    const argParams = args
      .map((arg) => `args=${encodeURIComponent(arg)}`)
      .join("&");
    return `/explain?env=${selectedEnv.pub_id}&${argParams}`;
  }
}

class LogErrorBoundary extends React.Component<
  React.PropsWithChildren<{ fallback: React.ReactNode }>,
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError(_error: Error) {
    // Update state so the next render will show the fallback UI.

    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback;
    }

    return this.props.children;
  }
}

const LogFallback: React.FC<{ log: any }> = ({ log }) => (
  <div className="flex items-start justify-between space-x-2">
    <div className="w-48 flex-shrink-0">
      <LogTiming log={log} />
    </div>
    <div className="flex-1">
      Raw log
      <pre className="text-xs whitespace-pre-wrap max-w-full">
        {JSON.stringify(log)}
      </pre>
    </div>
  </div>
);

const LogTiming: React.FC<{ log: Log }> = ({ log }) => (
  <>
    {log.timestamp && (
      <div>
        {formatDistanceToNow(new Date(log.timestamp))} ago
        <div className="text-gray-600 text-xs">
          {/* full date */}
          {format(new Date(log.timestamp), "yyyy-MM-dd h:mm:ssaaa")}
        </div>
      </div>
    )}
    {
      <div className="text-gray-600 text-xs mt-1">
        Took {log.fields.duration_ms}ms
      </div>
    }
  </>
);

const ViewSqlQueryButton: React.FC<{ sqlQuery: string }> = ({ sqlQuery }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <>
      <Button small onClick={() => setIsOpen(true)}>
        View
      </Button>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} wide>
        <div className="text-lg font-space">SQL query</div>
        <SyntaxHighlighter
          customStyle={{
            margin: 0,
            padding: "0.5rem",
            overflow: "auto",
            backgroundColor: BACKGROUND_COLOR,
          }}
          language="sql"
          style={THEME}
        >
          {sqlQuery}
        </SyntaxHighlighter>
      </Modal>
    </>
  );
};

const LogView: React.FC<{ log: Log; selectedEnv: EnvData | null }> = ({
  log,
  selectedEnv,
}) => {
  const hasContextFacts = log.fields.query.context_facts.length > 0;
  return (
    <div className="flex items-start justify-between space-x-2">
      <div className="w-48">
        <LogTiming log={log} />
      </div>
      <div className="flex-1">
        <FactView legacy fact={toFact(log.fields.query.fact)} />
        {isNonLocalAuthorize(log) && (
          <Button
            small
            href={
              hasContextFacts
                ? undefined
                : explainLink(log.fields.query, selectedEnv)
            }
            className="mt-1"
            title={
              hasContextFacts
                ? "Explain does not yet support context facts"
                : undefined
            }
            disabled={hasContextFacts}
          >
            Try this query &rarr;
          </Button>
        )}
      </div>
      <div className="w-2/12 flex-shrink-0">
        {log.fields.result_count === undefined ? (
          <>
            SQL query
            <div>
              <ViewSqlQueryButton
                sqlQuery={log.fields.sql_query || "Failed to load SQL query"}
              />
            </div>
          </>
        ) : isNonLocalAuthorize(log) ? (
          <>
            {log.fields.result_count > 0 ? (
              <span className="bg-green-600 text-white px-2 py-1 rounded font-bold uppercase text-xs">
                Allowed
              </span>
            ) : (
              <span className="bg-red-700 text-white px-2 py-1 rounded font-bold uppercase text-xs">
                Denied
              </span>
            )}
          </>
        ) : (
          <>
            Count:
            <div>{log.fields.result_count}</div>
          </>
        )}
      </div>
      {/* TODO: Logs with long context facts look kinda bad */}
      <div className="flex-shrink-0 w-2/12">
        {log.fields.query.context_facts.length > 0 ? (
          log.fields.query.context_facts.map((fact, i) => (
            <FactView legacy key={i} fact={fact} />
          ))
        ) : (
          <div className="text-gray-600 text-sm">None</div>
        )}
      </div>
    </div>
  );
};

const Logs: NextPage = () => {
  const { tenant } = useTenant();
  const { selectedEnv } = useEnv();
  // Array of search terms (tags)
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [successString, setSuccessString] = useState<string>("");
  const [sortField, setSortField] = useState<string>("timestamp");
  const [sortDirection, setSortDirection] = useState<string>("desc");
  // set when "search" is clicked or terms change
  const [logsQueryString, setLogsQueryString] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const onLogsFilter = useSegmentCallback("Logs - Search by Filter");
  const onLogsSuccess = useSegmentCallback("Logs - Search by Success/Failure");
  const onLogsDateChange = useSegmentCallback("Logs - Search by Date Range");

  // Update query string when search terms change
  React.useEffect(() => {
    const queryStr = searchTerms.join(" ");
    setLogsQueryString(queryStr);
  }, [searchTerms]);

  // load from query parameter for deeplinking
  const { query } = useRouter();
  React.useEffect(() => {
    if (query.q) {
      const terms = (query.q as string).split(" ").filter((t) => t.trim());
      setSearchTerms(terms);
      setLogsQueryString(query.q as string);
    }
    if (query.success) {
      if (query.success === "true" || query.success === "false") {
        setSuccessString(query.success as string);
      }
    }
  }, [query.q, query.success]);

  // Build the URL for fetching logs
  const getLogsUrl = useCallback(() => {
    if (!selectedEnv) return null;

    if (
      logsQueryString ||
      successString ||
      startDate ||
      endDate ||
      sortField ||
      sortDirection
    ) {
      const url = `/web/environment/${selectedEnv.pub_id}/logs?${
        logsQueryString ? `query=${logsQueryString}&` : ""
      }${successString ? `success=${successString}&` : ""}${
        startDate ? `since=${startDate.toISOString()}&` : ""
      }${
        endDate ? `before=${endDate.toISOString()}&` : ""
      }${`sort_by=${sortField}&`}${`sort_direction=${sortDirection}`}`;

      return url;
    }

    // For initial load or when all filters are cleared
    return `/web/environment/${selectedEnv.pub_id}/logs`;
  }, [
    selectedEnv,
    logsQueryString,
    successString,
    startDate,
    endDate,
    sortField,
    sortDirection,
  ]);

  const { data: logs, refresh, isLoading } = useData<Log[]>(getLogsUrl);

  const filtersApplied = useMemo(
    () => Boolean(logsQueryString || successString || startDate || endDate),
    [logsQueryString, successString, startDate, endDate]
  );

  const handleDateRangeChange = (
    newStartDate: Date | null,
    newEndDate: Date | null
  ) => {
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  };

  const handleApplyDateFilters = () => {
    onLogsDateChange();
    refresh();
  };

  if (!selectedEnv || !tenant) {
    return (
      <WithSidebar>
        <LoadingPage />
      </WithSidebar>
    );
  }

  return (
    <>
      <Head>
        <title>Oso Cloud - Logs</title>
      </Head>

      <WithSidebar>
        <MantineProvider>
          <MainContent>
            <div className="flex flex-wrap items-start justify-between">
              <div className="font-space text-3xl">Logs</div>
              <div className="flex space-x-4">
                <Button
                  medium
                  icon={<HiOutlineRefresh />}
                  onClick={() => refresh()}
                >
                  Refresh
                </Button>
              </div>
            </div>
            <div className="mt-2 mb-5">
              Oso logs all queries made by your applications, including their
              results and any context facts passed along with them.
              {tenant.kind !== "production" ? (
                <div className="mt-2">
                  Free tenants can see the last 24 hours of logs. Paid plans
                  offer longer retention and retrieval periods. To learn more,{" "}
                  <Schedule1x1Cta
                    utmContent="logs-page"
                    utmSource="app"
                    className="underline text-blue-600"
                  >
                    set up a call with an Oso Engineer
                  </Schedule1x1Cta>
                  .
                </div>
              ) : null}
            </div>
            {(Array.isArray(logs) && logs.length > 0) || filtersApplied ? (
              <>
                <div className="mt-2 mb-5 flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-[280px]">
                    <MultiSelect
                      placeholder="Search by rule name, argument type, ID, and X-Request-Id"
                      data={searchTerms}
                      value={searchTerms}
                      searchValue={searchValue}
                      onSearchChange={setSearchValue}
                      onChange={(values) => {
                        setSearchTerms(values);
                        onLogsFilter();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && searchValue.trim()) {
                          e.preventDefault();
                          const trimmed = searchValue.trim();
                          if (!searchTerms.includes(trimmed)) {
                            setSearchTerms([...searchTerms, trimmed]);
                            setSearchValue("");
                            onLogsFilter();
                          }
                        }
                      }}
                      searchable
                      creatable
                      getCreateLabel={(query) => `+ Add "${query}"`}
                      onCreate={(query) => {
                        const trimmed = query.trim();
                        if (trimmed && !searchTerms.includes(trimmed)) {
                          const newTerms = [...searchTerms, trimmed];
                          setSearchTerms(newTerms);
                          setSearchValue("");
                          return trimmed;
                        }
                        return null;
                      }}
                      clearable={false}
                      classNames={{
                        input: "mantine-multiselect-input",
                      }}
                      styles={() => ({
                        input: {
                          borderRadius: "0.25rem",
                          border: "1px solid #ced4da",
                          minHeight: "36px",
                          "&:focus-within": {
                            borderColor: "#352990",
                            borderWidth: "2px",
                            outline: "none",
                          },
                          "&:focus": {
                            outline: "none",
                          },
                        },
                        value: {
                          backgroundColor: "rgba(53, 41, 144, 0.08)",
                          color: "#352990",
                          border: "1px solid rgba(53, 41, 144, 0.3)",
                          borderRadius: "0.375rem",
                          padding: "3px 8px",
                          margin: "2px",
                          fontFamily: "ui-monospace, monospace",
                          fontSize: "0.875rem",
                          fontWeight: 500,
                        },
                        defaultValue: {
                          backgroundColor: "rgba(53, 41, 144, 0.08)",
                          color: "#352990",
                        },
                        searchInput: {
                          "&::placeholder": {
                            color: "#999",
                          },
                          "&:focus": {
                            boxShadow: "none !important",
                          },
                        },
                        rightSection: {
                          display: searchTerms.length > 0 ? "flex" : "none",
                        },
                        wrapper: {
                          "&:focus-within": {
                            outline: "none",
                          },
                        },
                      })}
                      rightSection={
                        searchTerms.length > 0 ? (
                          <CloseButton
                            onClick={() => {
                              setSearchTerms([]);
                              setSuccessString("");
                              setStartDate(null);
                              setEndDate(null);
                            }}
                            size="sm"
                            name="clear"
                          />
                        ) : null
                      }
                    />
                    <div className="text-xs text-gray-600 mt-1">
                      Type a term and press Enter to add. Searches across rule
                      names, types, IDs, and request IDs. Use Type:ID format for
                      exact pairs (e.g., User:alice).
                    </div>
                  </div>
                  <div className="min-w-[150px]">
                    <Select
                      placeholder="Result"
                      value={successString}
                      onChange={(value) => {
                        setSuccessString(value || "");
                        onLogsSuccess();
                      }}
                      data={[
                        { value: "", label: "All Results" },
                        { value: "true", label: "Allowed" },
                        { value: "false", label: "Denied" },
                      ]}
                      clearable
                      classNames={{
                        input: "mantine-select-input",
                      }}
                      styles={sharedSelectStyles}
                    />
                  </div>
                  <div className="min-w-[100px]">
                    <Select
                      classNames={{
                        input: "mantine-select-input",
                      }}
                      styles={sharedSelectStyles}
                      placeholder="Sort by"
                      value={`${sortField}:${sortDirection}`}
                      onChange={(value) => {
                        if (value) {
                          const [sortField, sortDirection] = value.split(":");
                          setSortField(sortField);
                          setSortDirection(sortDirection);
                        }
                      }}
                      data={[
                        { value: "timestamp:desc", label: "Newest" },
                        { value: "timestamp:asc", label: "Oldest" },
                        { value: "duration:desc", label: "Slowest" },
                        { value: "duration:asc", label: "Fastest" },
                      ]}
                    />
                  </div>
                  <div className="min-w-[280px]">
                    <DateRangePicker
                      startDate={startDate}
                      endDate={endDate}
                      onRangeChange={handleDateRangeChange}
                      onApplyFilters={handleApplyDateFilters}
                    />
                  </div>
                </div>
                <div className="flex items-start justify-between space-x-2 font-bold text-lg">
                  <div className="w-48">Time</div>
                  <div className="flex-1">Query</div>
                  <div className="w-2/12 flex-shrink-0">Result</div>
                  <div className="flex-shrink-0 w-2/12">Context</div>
                </div>
              </>
            ) : null}
            {isLoading ? (
              <div className="flex mt-10 text-medium justify-center text-gray-600">
                <Spinner />
              </div>
            ) : (
              <>
                {Array.isArray(logs) &&
                  logs.map((log) => (
                    <div key={log.id}>
                      {log.fields._type === "query" ? (
                        <div className="border-b py-4">
                          <LogErrorBoundary
                            fallback={<LogFallback log={log} />}
                          >
                            <LogView log={log} selectedEnv={selectedEnv} />
                          </LogErrorBoundary>
                        </div>
                      ) : // TODO: show non-query logs?
                      null}
                    </div>
                  ))}
                {Array.isArray(logs) && logs.length === 0 && (
                  <div className="flex flex-col items-center pt-10">
                    <div className="text-gray-400 text-6xl">
                      <AiOutlineFileSearch />
                    </div>
                    <div className="mt-4" />
                    {filtersApplied ? (
                      <div className="text-gray-600 text-xl">
                        No logs found matching your filters.
                      </div>
                    ) : (
                      <>
                        <div className="text-gray-600 text-xl">
                          No logs in the last 24 hours.
                        </div>
                        <GetStartedCta />
                        <div className="mt-4" />
                        <div className="text-gray-600">
                          To generate logs, run queries in this environment.
                        </div>
                        <div className="mt-2" />
                        <div className="text-gray-600">
                          Example:{" "}
                          <InlineCode>
                            oso-cloud authorize User:bob read Repository:anvils
                          </InlineCode>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </>
            )}
          </MainContent>
        </MantineProvider>
      </WithSidebar>
    </>
  );
};

export default Logs;
